import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { CheckCircle2, XCircle, Clock, FileText, Download, ExternalLink, Search, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '@/config/api';

const STEP_ORDER = ['proof_of_product', 'proof_of_funds', 'spa_execution'];
const STEP_SHORT = {
  proof_of_product: 'POP',
  proof_of_funds: 'POF',
  spa_execution: 'SPA',
};
const STATUS_CLS = {
  pending:     'text-[#94A3B8] bg-white/5 border-[#1E293B]',
  uploaded:    'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/40',
  in_progress: 'text-[#F59E0B] bg-[#F59E0B]/10 border-[#F59E0B]/40',
  verified:    'text-[#10B981] bg-[#10B981]/10 border-[#10B981]/40',
  rejected:    'text-[#EF4444] bg-[#EF4444]/10 border-[#EF4444]/40',
};

export default function ChecklistsAdminTab() {
  const { getAuthHeader } = useAuth();
  const [data, setData] = useState({ counters: null, checklists: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [detail, setDetail] = useState(null);
  const [rejecting, setRejecting] = useState(null); // {deal_id, step}
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/admin/checklists`, { headers: getAuthHeader() });
      setData(r.data);
    } catch (err) {
      toast.error('Failed to load checklists');
    } finally { setLoading(false); }
  }, [getAuthHeader]);
  useEffect(() => { load(); }, [load]);

  const q = search.trim().toLowerCase();
  const visible = data.checklists.filter((c) => {
    if (!q) return true;
    const hay = [
      c.deal?.mineral_title, c.seller?.name, c.seller?.email,
      c.buyer?.name, c.buyer?.email, c.deal_id,
    ].join(' ').toLowerCase();
    return hay.includes(q);
  });

  const verify = async (dealId, step) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/admin/checklist/${dealId}/${step}/verify`,
        { notes: '' }, { headers: getAuthHeader() });
      toast.success(`${step.replace(/_/g, ' ')} verified`);
      setDetail(r.data);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verify failed');
    } finally { setBusy(false); }
  };

  const submitReject = async () => {
    if (reason.trim().length < 3) return toast.error('Reason too short');
    setBusy(true);
    try {
      const { deal_id, step } = rejecting;
      const r = await axios.post(`${API_URL}/admin/checklist/${deal_id}/${step}/reject`,
        { reason: reason.trim() }, { headers: getAuthHeader() });
      toast.success(`${step.replace(/_/g, ' ')} rejected`);
      setRejecting(null); setReason('');
      setDetail(r.data);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : d?.message || 'Reject failed');
    } finally { setBusy(false); }
  };

  const downloadStep = async (dealId, step, fname) => {
    try {
      const r = await axios.get(`${API_URL}/deals/${dealId}/checklist/${step}/download`,
        { headers: getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = fname || `${step}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (loading) {
    return <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 text-[#F59E0B] animate-spin" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="admin-checklists-tab">
      {/* Counter cards */}
      {data.counters && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {STEP_ORDER.map((step) => {
            const c = data.counters[step] || {};
            return (
              <Card key={step} className="bg-[#121A2F] border-[#1E293B] p-4"
                    data-testid={`admin-checklist-counter-${step}`}>
                <p className="text-xs uppercase text-[#94A3B8] tracking-wider flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3" /> {STEP_SHORT[step]} · {step.replace(/_/g, ' ')}
                </p>
                <div className="mt-2 flex gap-3 flex-wrap text-xs">
                  {Object.entries(c).map(([status, n]) => (
                    <span key={status}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border ${STATUS_CLS[status] || STATUS_CLS.pending}`}>
                      {status}: <b>{n}</b>
                    </span>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)}
               placeholder="Search deal, seller, buyer, mineral…"
               data-testid="admin-checklists-search"
               className="pl-9 bg-[#121A2F] border-[#1E293B] text-white h-9 text-sm" />
      </div>

      {/* Table */}
      <Card className="bg-[#121A2F] border-[#1E293B] p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-checklists-table">
            <thead className="bg-[#1A2440] border-b border-[#1E293B]">
              <tr>{['Deal / Mineral', 'Parties', 'POP', 'POF', 'SPA', 'Progress', 'Actions'].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs uppercase tracking-wider text-[#94A3B8] whitespace-nowrap">{h}</th>
              ))}</tr>
            </thead>
            <tbody>
              {visible.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-[#64748B]">No checklists match your filters</td></tr>
              ) : visible.map((c) => (
                <tr key={c.deal_id} className="border-b border-[#1E293B] hover:bg-white/[0.02]"
                    data-testid={`admin-checklist-row-${c.deal_id}`}>
                  <td className="px-4 py-3 max-w-[240px]">
                    <p className="text-white truncate">{c.deal?.mineral_title || '—'}</p>
                    <p className="text-[10px] text-[#64748B] truncate">{c.deal_id}</p>
                  </td>
                  <td className="px-4 py-3 text-xs">
                    <p className="text-white">{c.seller?.name}</p>
                    <p className="text-[#64748B]">→ {c.buyer?.name}</p>
                  </td>
                  {STEP_ORDER.map((step) => {
                    const st = c.steps?.[step] || { status: 'pending' };
                    return (
                      <td key={step} className="px-4 py-3">
                        <span className={`inline-block text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full border ${STATUS_CLS[st.status] || STATUS_CLS.pending}`}
                              data-testid={`admin-checklist-${step}-${c.deal_id}`}>
                          {st.status.replace(/_/g, ' ')}
                        </span>
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-xs text-[#F59E0B] font-semibold">{c.percent_complete}%</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => setDetail(c)}
                              data-testid={`admin-checklist-view-${c.deal_id}`}
                              className="bg-transparent border-[#1E293B] text-white h-7 px-2 text-xs">View</Button>
                      <Button size="sm" variant="outline"
                              onClick={() => window.open(`/deals/${c.deal_id}`, '_blank')}
                              data-testid={`admin-checklist-open-${c.deal_id}`}
                              className="bg-transparent border-[#1E293B] text-white h-7 px-2 text-xs">
                        <ExternalLink className="w-3 h-3" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#121A2F] border-[#1E293B] text-white max-w-2xl"
                       data-testid="admin-checklist-detail-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Checklist detail</DialogTitle>
            <DialogDescription className="text-[#94A3B8] text-xs">
              {detail?.deal?.mineral_title} · Deal {detail?.deal_id?.slice(0, 8)}
            </DialogDescription>
          </DialogHeader>
          {detail && STEP_ORDER.map((step) => {
            const st = detail.steps[step] || { status: 'pending' };
            const cls = STATUS_CLS[st.status] || STATUS_CLS.pending;
            const isManual = step !== 'spa_execution';
            return (
              <div key={step} className="border border-[#1E293B] rounded-lg p-3 mt-2">
                <div className="flex justify-between items-start flex-wrap gap-2">
                  <div>
                    <p className="text-white font-medium text-sm capitalize">{step.replace(/_/g, ' ')}</p>
                    {st.file_name && (
                      <p className="text-xs text-[#94A3B8] mt-1 flex items-center gap-1">
                        <FileText className="w-3 h-3" /> {st.file_name}
                      </p>
                    )}
                    {st.rejection_reason && (
                      <p className="text-xs text-[#FCA5A5] mt-1">Reason: {st.rejection_reason}</p>
                    )}
                    {step === 'spa_execution' && st.notes && (
                      <p className="text-xs text-[#94A3B8] mt-1">{st.notes}</p>
                    )}
                  </div>
                  <span className={`text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full border ${cls}`}>
                    {st.status.replace(/_/g, ' ')}
                  </span>
                </div>
                {isManual && st.file_url && (
                  <div className="mt-3 flex gap-2 flex-wrap">
                    <Button size="sm" variant="outline"
                            onClick={() => downloadStep(detail.deal_id, step, st.file_name)}
                            data-testid={`admin-checklist-detail-download-${step}`}
                            className="bg-transparent border-[#1E293B] text-white h-8 text-xs">
                      <Download className="w-3 h-3 mr-1" />Download
                    </Button>
                    {st.status !== 'verified' && (
                      <Button size="sm" onClick={() => verify(detail.deal_id, step)} disabled={busy}
                              data-testid={`admin-checklist-verify-${step}`}
                              className="bg-[#10B981] hover:bg-[#059669] text-white font-semibold h-8 text-xs">
                        <CheckCircle2 className="w-3 h-3 mr-1" />Verify
                      </Button>
                    )}
                    {st.status !== 'rejected' && (
                      <Button size="sm" variant="outline"
                              onClick={() => setRejecting({ deal_id: detail.deal_id, step })} disabled={busy}
                              data-testid={`admin-checklist-reject-${step}`}
                              className="bg-transparent border-[#EF4444]/40 text-[#EF4444] hover:bg-[#EF4444]/10 h-8 text-xs">
                        <XCircle className="w-3 h-3 mr-1" />Reject
                      </Button>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={!!rejecting} onOpenChange={(o) => { if (!o) { setRejecting(null); setReason(''); } }}>
        <DialogContent className="bg-[#121A2F] border-[#1E293B] text-white max-w-md"
                       data-testid="admin-checklist-reject-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Reject document</DialogTitle>
            <DialogDescription className="text-[#94A3B8]">
              The uploader can re-upload with corrections. Don't include contact info.
            </DialogDescription>
          </DialogHeader>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)}
                    data-testid="admin-checklist-reject-reason-input"
                    className="bg-[#0B1120] border-[#1E293B] text-white"
                    placeholder="e.g. Statement is older than 30 days; please upload a current one." />
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => { setRejecting(null); setReason(''); }}
                    className="bg-transparent border-[#1E293B] text-white">Cancel</Button>
            <Button onClick={submitReject} disabled={busy}
                    data-testid="admin-checklist-reject-confirm"
                    className="bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold">Reject</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
